/**
 * `/ui` - every primitive this app has, on one page, in both themes.
 *
 * Not a style guide in prose (`docs/design/ui.md` is that) and not a component library:
 * it is the **inventory**. Its job is that nobody - person or agent - invents a
 * fourth kind of button because they could not find the three that exist, and
 * that a token or a state that has quietly broken is visible in one screenshot
 * rather than on whichever screen happens to use it.
 *
 * It is deliberately built out of the *real* classes and the *real* components,
 * never copies of them. A swatch that stops matching the app is then a bug in
 * the app, not in this page.
 *
 * Its own route, like `/admin`, and linked from nowhere. On a deployment it
 * needs an administrative permission - it exposes nothing, but a page listing
 * every control in the product is not part of anyone's job here. Locally it is
 * always open, because that is where it earns its keep: `make ui-shot ROUTE=/ui`
 * renders it headless in both themes.
 */

import { tableTemplate } from '../plot/render/render-table.ts';
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { confirmAction } from '../../ui/confirm-dialog.ts';
import { showToast } from '../../ui/toast.ts';
import { getTheme, toggleTheme } from '../../ui/theme.ts';
import { canAdminister } from '../../state/admin-access.ts';
import '../dashboard/dash-dropdown.ts';
import uPlot from 'uplot';
import { defaultPlotState } from '../../state/plot-state.ts';
import { buildNumberOpts } from '../plot/render/render-number.ts';
import { buildGaugeOpts } from '../plot/render/render-gauge.ts';
import { newGaugeDial } from '../plot/gauge-config.ts';
import { buildSankeyOpts } from '../plot/render/render-sankey.ts';
import type { SankeyNodeState } from '../plot/sankey-config.ts';
import { themeColors } from '../plot/render/chart-dom.ts';
import { PLOT_TYPES, plotTypeIcon } from '../plot/plot-type-icons.ts';
import { assistantMark } from '../assistant/mark.ts';
import '../../ui/unit-picker.ts';
import '../../ui/hold-switch.ts';
import { lockedNav } from '../../ui/locked-nav.ts';
import { clashesOf, colourPair, contrast, DARK_GROUND, LIGHT_GROUND } from '../../ui/colour-pair.ts';
import '../../ui/number-formula-editor.ts';
import '../../ui/layout-select.ts';
import '../../ui/live-chip.ts';
import type { ChartLayout } from '../../ui/layout-select.ts';

/** True when the browser is on `/ui`. A path, like `/admin`, not a param. */
export function isUiGalleryRoute(): boolean {
  if (typeof location === 'undefined') return false;
  return location.pathname.replace(/\/+$/, '') === '/ui';
}

/** Open locally to anyone; on a deployment, to whoever administers something. */
export function uiGalleryAllowed(): boolean {
  return import.meta.env.DEV || canAdminister();
}

/** The colour tokens, as they are defined in `tokens.css`. */
const COLOUR_TOKENS = [
  '--bg',
  '--surface',
  '--surface2',
  '--border',
  '--border-hover',
  '--text',
  '--text-dim',
  '--text-faint',
  '--accent',
  '--accent-hover',
  '--accent-press',
  '--danger',
  '--warning',
  '--success',
  '--tag-num',
  '--tag-str',
];

@customElement('ui-gallery')
export class UiGallery extends LightElement {
  @state() private busy = false;
  @state() private demoUnit = 'kW';
  @state() private galleryLayout: ChartLayout = 'overlay';
  @state() private demoColour = '#1F6F78';
  @state() private demoFormula = 'SUM("Fuel") / SUM("Distance")';
  private numberCharts: uPlot[] = [];
  private numberResize?: ResizeObserver;
  private numberTheme?: MutationObserver;

  protected override firstUpdated(): void {
    const draw = (): void => {
      this.numberCharts.forEach((chart) => chart.destroy());
      this.numberCharts = [];
      const examples = [
        { value: 12345.67, label: 'Average generator power', unit: 'kW' },
        { value: -2.375, label: 'SUM("Fuel") / SUM("Distance")', unit: 't/nm' },
        { value: null, label: 'Latest speed', unit: 'kn' },
      ];
      this.querySelectorAll<HTMLElement>('.uig-number-preview').forEach((mount, i) => {
        const sample = examples[i]!;
        const state = { ...defaultPlotState(), number_unit: sample.unit, number_label: i === 1 ? '' : sample.label };
        const { opts, uData } = buildNumberOpts({
          plot_type: 'number', value: sample.value, label: sample.label,
          source_rows: sample.value == null ? 0 : 1000, valid_counts: {}, warning: null,
        }, themeColors(), {width: mount.clientWidth, height: mount.clientHeight}, state);
        this.numberCharts.push(new uPlot(opts, uData, mount));
      });
      // Gauges: a console in every state a dial has - normal, yellow, red past
      // the end stop, and a signal the ship does not carry.
      const dials = [
        { ...newGaugeDial('ME1_Load'), label: 'Main engine load', unit: '%', decimals: 1, min: 0, max: 100,
          zones: [{ color: 'yellow' as const, from: 85, to: 95 }, { color: 'red' as const, from: 95, to: null }] },
        { ...newGaugeDial('Hotel_Load_MW'), label: 'Hotel load', unit: 'MW', min: 0, max: 14,
          zones: [{ color: 'yellow' as const, from: 10, to: 12 }, { color: 'red' as const, from: 12, to: null }] },
        { ...newGaugeDial('SOG'), label: 'Speed', unit: 'kn', decimals: 1 },
      ];
      const shipDials = (load: number, hotel: number, sog: number | null) => [
        { key: 'latest:ME1_Load', value: load, valid_count: 1, missing: [] },
        { key: 'latest:Hotel_Load_MW', value: hotel, valid_count: 1, missing: [] },
        { key: 'latest:SOG', value: sog, valid_count: sog == null ? 0 : 1, missing: sog == null ? ['SOG'] : [] },
      ];
      this.querySelectorAll<HTMLElement>('.uig-gauge-preview').forEach((mount, i) => {
        const state = { ...defaultPlotState(), plot_type: 'gauge' as const, gauge_dials: dials, gauge_layout: 'matrix' as const };
        const data = i === 0
          ? { plot_type: 'gauge' as const, dials: shipDials(88.1, 7.49, 18.2), source_rows: 1000 }
          : { plot_type: 'gauge' as const, dials: [], source_rows: 2000, ships: [
              { ship: 'A-CS', dials: shipDials(70.8, 12.6, 18.2) },
              { ship: 'P-RU', dials: shipDials(104.2, 9.4, null) },
            ] };
        const { opts, uData } = buildGaugeOpts(data, themeColors(), { width: mount.clientWidth, height: mount.clientHeight }, state);
        this.numberCharts.push(new uPlot(opts, uData, mount));
      });
      // Sankeys: Thruster Explorer's power flow, with a remainder and without
      // one (the hatched stub and the note say what has no ribbon).
      const sn = (id: string, col: number, p: Partial<SankeyNodeState>): SankeyNodeState => ({
        id, kind: 'signal', column: '', agg: 'avg', expression: '', label: '', col, to: [], ...p,
      });
      const flow = [
        sn('n1', 0, { column: 'DG1_Power', label: 'DG1', to: ['n5'] }),
        sn('n2', 0, { column: 'DG2_Power', label: 'DG2', to: ['n5'] }),
        sn('n3', 0, { column: 'DG3_Power', label: 'DG3', to: ['n5'] }),
        sn('n4', 0, { column: 'DG4_Power', label: 'DG4', to: ['n5'] }),
        sn('n5', 1, { kind: 'sum', label: 'Switchboard', to: ['n6', 'n7', 'n8', 'n9'] }),
        sn('n6', 2, { column: 'BT_Power', label: 'Bow thrusters' }),
        sn('n7', 2, { column: 'ST_Power', label: 'Stern thrusters' }),
        sn('n8', 2, { column: 'Prop_Power', label: 'Propulsion' }),
        sn('n9', 2, { kind: 'rest', label: 'Hotel & other' }),
      ];
      const flowValues = Object.entries({ DG1_Power: 3200, DG2_Power: 3100, DG3_Power: 0, DG4_Power: 2900, BT_Power: 1200, ST_Power: 600, Prop_Power: 4100 })
        .map(([c, value]) => ({ key: `avg:${c}`, value, valid_count: 1, missing: [] }));
      this.querySelectorAll<HTMLElement>('.uig-sankey-preview').forEach((mount, i) => {
        const nodes = i === 0 ? flow : flow.filter((n) => n.id !== 'n9').map((n) => (n.id === 'n5' ? { ...n, to: ['n6', 'n7', 'n8'] } : n));
        const state = { ...defaultPlotState(), plot_type: 'sankey' as const, sankey_nodes: nodes, sankey_display: { unit: 'kW', decimals: 0, motion: true } };
        const { opts, uData } = buildSankeyOpts({ plot_type: 'sankey', dials: flowValues, source_rows: 1000 },
          themeColors(), { width: mount.clientWidth, height: mount.clientHeight }, undefined, state);
        this.numberCharts.push(new uPlot(opts, uData, mount));
      });
    };
    this.numberResize = new ResizeObserver(draw);
    this.querySelectorAll('.uig-number-preview, .uig-gauge-preview, .uig-sankey-preview').forEach((mount) => this.numberResize!.observe(mount));
    this.numberTheme = new MutationObserver(() => { draw(); this.requestUpdate(); });
    this.numberTheme.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }

  override disconnectedCallback(): void {
    this.numberResize?.disconnect();
    this.numberTheme?.disconnect();
    this.numberCharts.forEach((chart) => chart.destroy());
    super.disconnectedCallback();
  }

  private async demoConfirm(): Promise<void> {
    const ok = await confirmAction({
      title: 'Discard changes?',
      message: 'This is the confirm dialog every destructive action in the app uses.',
      confirmLabel: 'Discard',
      destructive: true,
    });
    showToast(ok ? 'success' : 'error', ok ? 'Confirmed' : 'Cancelled');
  }

  private section(title: string, note: string, body: TemplateResult): TemplateResult {
    return html`
      <section class="uig-section">
        <header class="uig-head">
          <h2>${title}</h2>
          <p>${note}</p>
        </header>
        <div class="uig-body">${body}</div>
      </section>
    `;
  }

  override render(): TemplateResult {
    if (!uiGalleryAllowed()) {
      return html`<div class="uig"><p class="admin-note">This page needs an administrative permission.</p></div>`;
    }
    return html`
      <div class="uig">
        <header class="uig-top">
          <div>
            <h1>UI inventory</h1>
            <p class="admin-note">
              Every primitive in the app, from the real classes. Use what is here; if
              something is missing, add it here in the same change - see
              <code>docs/design/ui.md</code>.
            </p>
          </div>
          <div class="uig-top-actions">
            <button class="btn btn-secondary" @click=${() => { toggleTheme(); this.requestUpdate(); }}>
              Theme: ${getTheme()}
            </button>
            <a class="btn btn-secondary" href="/">← Back to app</a>
          </div>
        </header>

        ${this.section(
          'Gauge charts',
          'The real Gauge renderer (render-gauge.ts): one ship\'s console, and two ships as a matrix - a yellow reading, a red one past the end stop, and a signal a ship does not carry. The dial list that edits them is gauge-controls.ts in the Plot card.',
          html`<div class="uig-gauge-grid">
            <div class="uig-gauge-preview"></div>
            <div class="uig-gauge-preview"></div>
          </div>`,
        )}

        ${this.section(
          'Sankey charts',
          'The real Sankey renderer (render-sankey.ts), look A: Thruster Explorer\'s power flow with a Remainder node, and without one - the hatched stub and the note name the part with no ribbon. Hover a node or a ribbon: it and what it joins light up, the rest fades. The node list that edits them is sankey-controls.ts in the Plot card.',
          html`<div class="uig-sankey-grid">
            <div class="uig-sankey-preview"></div>
            <div class="uig-sankey-preview"></div>
          </div>`,
        )}

        ${this.section(
          'Number charts',
          'The real Number renderer: a labelled value, a calculation with no caption, and an empty range. Uses the theme typography and colours.',
          html`<div class="uig-number-grid">
            <div class="uig-number-preview"></div>
            <div class="uig-number-preview"></div>
            <div class="uig-number-preview"></div>
          </div>
          <div class="plot-type-grid">
            ${PLOT_TYPES.map((type) => html`<button class="plot-type-btn ${type.value === 'number' ? 'active' : ''}">
              ${plotTypeIcon(type.value)}<span class="plot-type-name">${type.label}</span>
            </button>`)}
          </div>
          <div class="axis-block">
            <label>Unit picker: search symbols, names or categories, or choose Custom</label>
            <unit-picker .value=${this.demoUnit} .select=${(value: string) => { this.demoUnit = value; }}></unit-picker>
          </div>
          <div class="axis-block">
            <number-formula-editor .value=${this.demoFormula}
              .columns=${['Fuel', 'Distance', 'Generator power'].map((name) => ({ name, type: 'DOUBLE', null_pct: 0, total_count: 1000, null_count: 0 }))}
              .change=${(value: string) => { this.demoFormula = value; }}></number-formula-editor>
          </div>`,
        )}

        ${this.section(
          'Locked navigation entry',
          'ui/locked-nav.ts: a place that exists but not for this account - struck through, a not-allowed cursor, and on hover or focus a stop sign with whom to ask. Hover the entries. Hidden instead when the feature is off for everyone.',
          html`<div class="uig-row">
            <nav class="primary-nav uig-locked-demo">
              <button class="primary-nav-btn">Explore</button>
              ${lockedNav('ARGUS', 'You do not have access to Argus. Ask an administrator for an Argus role.', 'primary-nav-btn')}
            </nav>
            <nav class="tab-bar contextual-tabs uig-locked-demo">
              <button class="context-tab tab-btn active">Plot</button>
              ${lockedNav('Export', 'You do not have access to Export. Ask an administrator to grant it.', 'context-tab tab-btn')}
            </nav>
          </div>`,
        )}

        ${this.section(
          'Colour tokens',
          'Defined in styles/tokens.css, redefined per theme. Never write a literal colour.',
          html`<div class="uig-swatches">
            ${COLOUR_TOKENS.map(
              (token) => html`<div class="uig-swatch">
                <span class="uig-chip" style="background: var(${token})"></span>
                <code>${token}</code>
              </div>`,
            )}
          </div>`,
        )}

        ${this.section(
          'Live chip',
          '<live-chip> on a chart or tile drawn from a live source: the age of the newest reading counts up by itself. mode: ticking (the dot breathes; a button that pauses), paused (a button that resumes), static (a fixed window; not a button). Past stale-after (180 s) the dot goes hollow and amber and the tooltip says nothing newer has arrived.',
          html`<div class="uig-row">
            <live-chip mode="ticking" .cadence=${10} .newest=${Date.now() / 1000 - 4}></live-chip>
            <live-chip mode="paused" .newest=${Date.now() / 1000 - 40}></live-chip>
            <live-chip .newest=${Date.now() / 1000 - 3 * 3600}></live-chip>
            <live-chip></live-chip>
          </div>`,
        )}

        ${this.section(
          'Buttons',
          '.btn is the base; .btn-primary for the one action that commits, .btn-secondary for the rest. Feature areas add their own (.dash-btn, .catalog-btn) - do not invent a fourth.',
          html`<div class="uig-row">
            <button class="btn btn-primary">Primary</button>
            <button class="btn btn-secondary">Secondary</button>
            <button class="btn btn-primary" disabled>Disabled</button>
            <button class="dash-btn">Dashboard</button>
            <button class="dash-btn is-primary">Dashboard primary</button>
            <button class="dash-btn is-dirty">Unsaved</button>
            <button class="dash-btn" disabled>Saved</button>
          </div>`,
        )}

        ${this.section(
          'Home assistant invitation',
          'The full-width action that opens SONAR from Home. It uses the same compass mark as the app header.',
          html`<button class="home-assistant">
            <span class="home-assistant-mark">${assistantMark(25)}</span>
            <span class="home-assistant-copy">
              <strong>Ask SONAR</strong>
              <span>Ask about ships and signals, or describe the chart you need.</span>
            </span>
            <span class="home-assistant-go" aria-hidden="true">Open chat <span>↗</span></span>
          </button>`,
        )}

        ${this.section(
          'Hold switch',
          'A switch that flips only when held (<hold-switch>): for switches whose flip reaches other people, such as deploying an alert. Holding slides the knob across over the hold time, the colour running from the left end to the knob (both from one animated --hs-p, so they never part); letting go early springs it back. A click only says "Hold to …". The owner answers hold-commit by changing checked. Colour comes from --hs-on.',
          html`<div class="uig-row">
            <hold-switch label="Example, off" @hold-commit=${(e: Event) => ((e.target as HTMLElement & { checked: boolean }).checked = !(e.target as HTMLElement & { checked: boolean }).checked)}></hold-switch>
            <hold-switch checked label="Example, on" @hold-commit=${(e: Event) => ((e.target as HTMLElement & { checked: boolean }).checked = !(e.target as HTMLElement & { checked: boolean }).checked)}></hold-switch>
            <hold-switch disabled label="Example, disabled"></hold-switch>
          </div>`,
        )}

        ${this.section(
          'Alert glow (firing states, coming)',
          'styles/components/alert-glow.css: a firing state breathes - a soft halo swelling and fading, 2 s - instead of blinking. Cards are tuned per severity and theme (--glow-halo, --glow-bord, --glow-aur, --glow-tint; docs/design/sketches/argus-glow-tuner.html). Add glow-alive to what should breathe, plus a shape: glow-chip (with glow-dot), glow-btn (is-ghost), glow-badge (glow-shake on the bell), glow-row, glow-card + glow-card-in, glow-edge + glow-edge-ring + glow-edge-in (runs its edge and breathes) for critical and watch alerts. Cards animate only opacity and transform, so fifty of them stay smooth. Colour: is-critical (default), is-watch, is-info. Everything breathes in step from one keyframe. Reduced motion keeps it all - the ping and running edge a little slower - and drops only the bell shake.',
          html`<div class="uig-stack">
            <div class="uig-row">
              <span class="glow-chip glow-alive"><span class="glow-dot"></span>Firing</span>
              <span class="glow-chip glow-alive is-watch"><span class="glow-dot"></span>Watch</span>
              <span class="glow-chip glow-alive is-info">Info</span>
              <button class="glow-btn glow-alive" type="button">Look now</button>
              <button class="glow-btn is-ghost glow-alive" type="button">Acknowledge</button>
              <span class="uig-glow-bell">
                <svg class="glow-shake" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2h-15z"/><path d="M10 20a2 2 0 0 0 4 0"/></svg>
                <span class="glow-badge glow-alive">3</span>
              </span>
            </div>
            <div class="uig-glow-row glow-row glow-alive">
              <strong>Chillers in mild weather</strong><span>AIDAnova · AC1 and AC2 running for 42 min</span>
            </div>
            <div class="uig-glow-grid">
              <div class="glow-card">
                <div class="glow-card-in uig-glow-card">
                  <strong>DG1 overload on AIDAcosma</strong>
                  <span>DG1_ACTIVE_POWER above 250 kW for 1 h 20 min.</span>
                </div>
              </div>
              <div class="glow-edge">
                <span class="glow-edge-ring" aria-hidden="true"></span>
                <div class="glow-edge-in">
                  <strong>Scrubber pump pressure</strong>
                  <span>Outside 2 to 6 bar on AIDAprima, 3 readings in a row.</span>
                </div>
              </div>
            </div>
          </div>`,
        )}

        ${this.section(
          'Colour pair (per-person colours, coming)',
          'ui/colour-pair.ts: someone picks any colour; colourPair() keeps its hue and returns the lightest shade readable on the light page and the darkest readable on a dark panel, and clashesOf() names the colours it could be mistaken for. Not wired to anything yet - it is here for the per-person Argus colour. Petrol gives the pair Argus uses.',
          (() => {
            const pair = colourPair(this.demoColour);
            const clashes = clashesOf(this.demoColour);
            return html`<div class="uig-row">
              <input
                type="color"
                .value=${this.demoColour}
                aria-label="Pick a colour"
                @input=${(e: Event) => (this.demoColour = (e.target as HTMLInputElement).value)}
              />
              <span class="uig-pair" style=${`background:${LIGHT_GROUND};color:${pair.light}`}
                >Light ${pair.light} · ${contrast(pair.light, LIGHT_GROUND).toFixed(1)}:1</span
              >
              <span class="uig-pair" style=${`background:${DARK_GROUND};color:${pair.dark}`}
                >Dark ${pair.dark} · ${contrast(pair.dark, DARK_GROUND).toFixed(1)}:1</span
              >
              <span class="uig-pair-note">${clashes.length ? clashes.join('; ') : 'Clear of every colour that already means something.'}</span>
            </div>`;
          })(),
        )}

        ${this.section(
          'Switches and toggles',
          'A checkbox drawn as a switch (.admin-switch), and the whole-row form used in panels (.dset-toggle).',
          html`
            <div class="uig-row">
              <input class="admin-switch" type="checkbox" role="switch" checked aria-label="On" />
              <input class="admin-switch" type="checkbox" role="switch" aria-label="Off" />
              <input class="admin-switch" type="checkbox" role="switch" disabled aria-label="Locked" />
            </div>
            <div class="uig-stack">
              <button class="dset-toggle is-on" role="switch" aria-checked="true">
                <span class="dset-toggle-text">
                  <strong>Shared crosshair</strong>
                  <span>On: the row explains what the state means, not what the switch is.</span>
                </span>
                <span class="sd-switch" aria-hidden="true"></span>
              </button>
              <button class="dset-toggle" role="switch" aria-checked="false">
                <span class="dset-toggle-text">
                  <strong>Float tiles up</strong>
                  <span>Off: tiles stay where you drop them.</span>
                </span>
                <span class="sd-switch" aria-hidden="true"></span>
              </button>
            </div>
          `,
        )}

        ${this.section(
          'Segmented control',
          'One of a few choices that change the view (.seg + .seg-btn) - the Data source step, Overlay / Grid, the SFOC measured layer. Several independent on/off choices are switches, not segments. A choice that exists but cannot be made yet takes aria-disabled="true" (not disabled, so its title - the reason - still shows on hover). .ds-seg stretches the segments across a card.',
          html`
            <div class="uig-row">
              <div class="seg" role="radiogroup" aria-label="Example">
                <button class="seg-btn active" role="radio" aria-checked="true">Scatter</button>
                <button class="seg-btn" role="radio" aria-checked="false">Heatmap</button>
                <button class="seg-btn" role="radio" aria-checked="false">Load</button>
              </div>
            </div>
            <div class="uig-row" style="max-width: 320px; margin-top: 0.75rem">
              <div class="seg ds-seg" role="radiogroup" aria-label="Data source example">
                <button class="seg-btn active" role="radio" aria-checked="true">EOS</button>
                <button class="seg-btn" role="radio" aria-checked="false" aria-disabled="true" title="Live data is not available right now.">
                  Live
                </button>
              </div>
            </div>
          `,
        )}

        ${this.section(
          'Layout selector',
          '<layout-select> - Layout → Overlay / Grid, the one control Explore and SFOC both use once several ships are drawn. Arrow keys move the choice; a disabled Overlay carries its reason as the title.',
          html`
            <div class="uig-row">
              <layout-select
                .value=${this.galleryLayout}
                .select=${(v: ChartLayout) => {
                  this.galleryLayout = v;
                }}
              ></layout-select>
              <layout-select
                value="grid"
                .overlayUnavailable=${'A heatmap cannot be overlaid - one panel per ship'}
              ></layout-select>
            </div>
          `,
        )}

        ${this.section(
          'Inputs',
          'Bare elements are styled in base.css - a text input needs no class. .dset-input is the panel variant.',
          html`<div class="uig-row">
            <input type="text" placeholder="Text" />
            <input type="number" .value=${'42'} />
            <input type="datetime-local" .value=${'2026-01-01T00:00'} />
            <select>
              <option>Select</option>
            </select>
            <input class="dset-input" .value=${'Panel input'} />
          </div>`,
        )}

        ${this.section(
          'Dashboard names and tabs',
          'In the list, hover a name to reveal its pencil. An open dashboard\'s name is its own rename button, and a tab\'s pencil takes the place of its chart count on hover - nothing holds empty space for a hidden pencil.',
          html`<div class="uig-stack">
            <span class="dbrowse-title-line">
              <span class="dbrowse-label">Fleet dashboards</span>
              <button class="dbrowse-title-edit" aria-label="Rename Fleet dashboards">✎</button>
            </span>
            <span class="dash-crumb-title">
              <button class="dash-crumb-current dash-name-edit" aria-label="Rename dashboard Fleet overview">Fleet overview</button>
            </span>
            <div class="dash-tabbar" role="tablist">
              <span class="dash-tab-wrap active">
                <span class="dash-tab-main">
                  <button class="dash-tab active" role="tab" aria-selected="true">
                    New tab <span class="dash-tab-count">0</span>
                  </button>
                  <button class="dash-tab-edit" aria-label="Rename New tab">✎</button>
                </span>
              </span>
              <button class="dash-tab-add" aria-label="Add tab">+</button>
            </div>
          </div>`,
        )}

        ${this.section(
          'Slicers (dashboard filters)',
          'A dashboard filter is built like a chart tile - .dash-tile, .dash-tile-head with .dash-tile-title and .dash-tile-btn actions (Clear ⟲ when narrowed; ✎ and ✕ in Edit mode) - with the filter as its body: Explore\'s .slicer-track and .slicer-readout for Range and Dates, a <dash-dropdown>, or .col-checkbox values with Apply (.dash-btn is-primary) once the ticks differ. Made in the Add filter dialog (dashboard-filter-dialog.ts): the kind as .plot-type-btn cards, the column in <column-combobox>, an optional label.',
          html`<div class="uig-row uig-slicers">
            <div class="dash-tile is-slicer is-list is-narrowed">
              <div class="dash-tile-head">
                <div class="dash-tile-heading"><span class="dash-tile-title">Operating mode</span></div>
                <div class="dash-tile-actions">
                  <button class="dash-tile-btn" aria-label="Clear filter">⟲</button>
                  <button class="dash-tile-btn" aria-label="Edit filter">✎</button>
                  <button class="dash-tile-btn is-danger" aria-label="Remove filter">✕</button>
                </div>
              </div>
              <div class="dash-slicer-body">
                <div class="dash-slicer-list">
                  <label class="dash-slicer-value"><input class="col-checkbox" type="checkbox" checked /><span>Sea</span></label>
                  <label class="dash-slicer-value"><input class="col-checkbox" type="checkbox" /><span>Port</span></label>
                  <label class="dash-slicer-value"><input class="col-checkbox" type="checkbox" checked /><span>Maneuvering</span></label>
                </div>
                <div class="dash-slicer-foot">
                  <button class="dash-btn is-primary">Apply</button>
                  <button class="dash-btn">Undo</button>
                </div>
              </div>
            </div>
            <div class="uig-stack uig-slicer-rows">
              <div class="dash-tile is-slicer is-range is-narrowed">
                <div class="dash-tile-head">
                <div class="dash-tile-heading"><span class="dash-tile-title">Engine speed</span></div>
                <div class="dash-tile-actions">
                  <button class="dash-tile-btn" aria-label="Clear filter">⟲</button>
                  <button class="dash-tile-btn" aria-label="Edit filter">✎</button>
                  <button class="dash-tile-btn is-danger" aria-label="Remove filter">✕</button>
                </div>
              </div>
                <div class="dash-slicer-body">
                  <div class="slicer-row dash-slicer-line is-narrowed">
                    <div class="slicer-track">
                      <div class="slicer-hist" aria-hidden="true">
                        ${[0.9, 0.5, 0.3, 0.25, 0.2, 0.3, 0.45, 0.7, 0.4, 0.3, 0.2, 0.6].map(
                          (f, i) => html`<span class=${i >= 3 && i <= 8 ? 'is-in' : ''} style=${`--fill:${f}`}></span>`,
                        )}
                      </div>
                      <div class="slicer-band" style="left:25%;width:50%">
                        <span class="slicer-handle is-lo"></span>
                        <span class="slicer-handle is-hi"></span>
                      </div>
                    </div>
                    <span class="slicer-readout">95.00 – 120.0</span>
                  </div>
                </div>
              </div>
              <div class="plot-type-grid">
                <button class="plot-type-btn active"><span class="plot-type-name">List</span></button>
                <button class="plot-type-btn"><span class="plot-type-name">Dropdown</span></button>
                <button class="plot-type-btn"><span class="plot-type-name">Range</span></button>
                <button class="plot-type-btn is-unavailable"><span class="plot-type-name">Dates</span></button>
              </div>
            </div>
          </div>`,
        )}

        ${this.section(
          'Tags and chips',
          '.admin-kind for a category, .admin-role-chip for standing, .ff-chip for a person or group; .activity-outcome labels audit results in words and colour.',
          html`<div class="uig-row">
            <span class="admin-kind">section</span>
            <span class="admin-kind is-root">root only</span>
            <span class="admin-role-chip is-admin">admin</span>
            <span class="admin-role-chip is-root">root</span>
            <span class="ff-chip">A person</span>
            <span class="ff-chip is-group">A group</span>
            <code class="admin-perm">dashboards:write</code>
            <span class="activity-outcome is-success">Succeeded</span>
            <span class="activity-outcome is-denied">Refused</span>
            <span class="activity-outcome is-error">Error</span>
          </div>`,
        )}

        ${this.section(
          'Tables',
          '.people-table-wrap and .people-table provide the scroll area, sticky headings and hairlines used by administration. Activity adds wrapping event text and explicit outcomes.',
          html`<div class="people-table-wrap activity-list">
            <table class="people-table activity-table" aria-label="Example activity table">
              <thead><tr><th scope="col">Time</th><th scope="col">Person</th><th scope="col">Event</th><th scope="col">Outcome</th></tr></thead>
              <tbody><tr>
                <td class="activity-when"><time datetime="2026-09-17T10:30:00Z"><span>17 Sept 2026</span><span class="people-muted">10:30:00</span></time></td>
                <td class="activity-actor"><span>A person</span></td>
                <td class="activity-what"><span>Shared chart</span><span class="activity-object">Main engine fuel consumption</span><span class="activity-summary">Fleet operations · viewer</span></td>
                <td><span class="activity-outcome is-success">Succeeded</span></td>
              </tr></tbody>
            </table>
          </div>`,
        )}

        ${this.section(
          'Data table',
          '.data-table is the Table plot type: sticky header, first column and total row, tabular figures, click a heading to sort. Drawn by tableTemplate in render-table.ts, in Explore and on dashboard tiles.',
          html`<div class="uig-data-table">${tableTemplate(
            {
              plot_type: 'table',
              mode: 'summary',
              columns: [],
              groups: [{ kind: 'time', bucket: 'day', column: null, edges: [] }],
              measures: [
                { agg: 'avg', column: 'STW', label: 'Average speed' },
                { agg: 'hours', where: '"DG1_LOAD" > 85', label: 'Hours above 85% load' },
                { agg: 'share', where: '"STW" > 15' },
              ],
              rows: [
                { keys: ['2026-08-01T00:00:00'], n: 480, values: [12.41, 3.2, 35.2] },
                { keys: ['2026-08-02T00:00:00'], n: 480, values: [9.07, 0, 8.54] },
                { keys: ['2026-08-03T00:00:00'], n: 479, values: [14.62, 11.75, null] },
              ],
              total: { n: 1439, values: [12.03, 14.95, 21.4] },
              rows_left_out: 0,
              source_rows: 1439,
            },
            { ...defaultPlotState(), table_sort: 'm1:desc' },
            { onSort: () => {}, download: true },
          )}</div>`,
        )}

        ${this.section(
          'Cards and headers',
          '.card is the sidebar block; .section-header is the small uppercase rule above a group.',
          html`<div class="uig-stack">
            <div class="card">
              <h2>Query<span class="req-mark" title="Required">*</span></h2>
              <p class="admin-note">A card holds one decision and its controls.</p>
            </div>
            <div class="section-header">Section header</div>
          </div>`,
        )}

        ${this.section(
          'Empty and unbuilt states',
          'An empty screen must say which kind of empty it is: nothing yet, nothing matching, or not built.',
          html`<div class="uig-stack">
            <div class="dbrowse-blank">
              <h2>No dashboards yet</h2>
              <p>What this is, and the one action that starts it.</p>
              <button class="dash-btn is-primary">New dashboard</button>
            </div>
            <div class="admin-unbuilt">
              <h2>Not built yet</h2>
              <p class="admin-note">Say what will be here and who will be allowed to use it.</p>
            </div>
          </div>`,
        )}

        ${this.section(
          'Overlays',
          'One confirm dialog (confirmAction) and one toast queue (showToast) for the whole app.',
          html`<div class="uig-row">
            <button class="btn btn-secondary" @click=${this.demoConfirm}>Confirm dialog</button>
            <button class="btn btn-secondary" @click=${() => showToast('success', 'Saved')}>
              Toast: success
            </button>
            <button class="btn btn-secondary" @click=${() => showToast('error', 'Could not save')}>
              Toast: error
            </button>
            <dash-dropdown
              caption="Dropdown"
              label="A panel"
              .panel=${() => html`<div class="uig-stack" style="padding: 0.6rem">
                <strong>Panel content</strong>
                <span class="admin-note">Dropdowns wrap existing components; they never reimplement them.</span>
              </div>`}
            ></dash-dropdown>
          </div>`,
        )}

        ${this.section(
          'Progress and waiting',
          'A bar for work with an end, a spinner for work without one. Never a label that changes word. One spinner (cell sweep): .spinner inline, .spinner-md for tiles and overlays, .spinner-lg for empty pages.',
          html`<div class="uig-row">
            <span class="spinner spinner-sm" aria-hidden="true"></span>
            <span class="spinner" aria-hidden="true"></span>
            <span class="spinner spinner-md" aria-hidden="true"></span>
            <span class="spinner spinner-lg" aria-hidden="true"></span>
            <button class="btn btn-primary" ?disabled=${this.busy} @click=${() => {
              this.busy = true;
              setTimeout(() => {
                this.busy = false;
              }, 1200);
            }}>
              ${this.busy ? html`<span class="spinner" aria-hidden="true"></span> Working` : 'Start'}
            </button>
          </div>`,
        )}

        ${this.section(
          'Typography',
          'Two families only: --font-sans for reading, --font-mono for anything a machine wrote (ids, signals, timestamps).',
          html`<div class="uig-stack">
            <h1>Heading one</h1>
            <h2>Heading two</h2>
            <p>Body copy, which is what most of the app is made of.</p>
            <p class="admin-note">A note: dimmer, smaller, and where the explaining goes.</p>
            <code>IMO9506291 · ME_FuelConsumption · 2026-09-15T10:13:36Z</code>
          </div>`,
        )}
      </div>
      ${nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ui-gallery': UiGallery;
  }
}
